import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowLeft, Edit, User, BookOpen, GraduationCap, Wallet, 
  ClipboardCheck, Phone, MapPin, Calendar, Users, CheckCircle2, 
  Clock, RotateCcw, TrendingUp, FileText, MessageCircle, Download, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api from '../lib/api';
import WhatsAppContact from '../components/WhatsAppContact';

export default function StudentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingCert, setDownloadingCert] = useState(null);

  useEffect(() => {
    fetchStudentDetail();
  }, [id]);

  const fetchStudentDetail = async () => {
    try {
      const response = await api.get(`/students/${id}/detail`);
      setData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/students');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async (type) => {
    setDownloadingCert(type);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/students/${id}/certificate/${type}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Erreur téléchargement');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificat_${type}_${id.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Certificat téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingCert(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(value);
  };

  const getStudentTypeBadge = (type) => {
    const badges = {
      quranic: { label: 'Coranique', class: 'badge-quranic', icon: BookOpen },
      classical: { label: 'Classique', class: 'badge-classical', icon: GraduationCap },
      mixed: { label: 'Mixte', class: 'badge-mixed', icon: Users },
    };
    return badges[type] || { label: type, class: '', icon: User };
  };

  const getQuranicLevelLabel = (level) => {
    const levels = { iqra: 'Iqra', juz_amma: 'Juz Amma', '5_juz': '5 Juz', '10_juz': '10 Juz', '15_juz': '15 Juz', '20_juz': '20 Juz', hafiz: 'Hafiz' };
    return levels[level] || level || '-';
  };

  const getStatusInfo = (status) => {
    const statuses = {
      not_started: { label: 'Non commencé', class: 'status-not-started', icon: Clock },
      in_progress: { label: 'En cours', class: 'status-in-progress', icon: Clock },
      acquired: { label: 'Acquis', class: 'status-acquired', icon: CheckCircle2 },
      revision: { label: 'Révision', class: 'status-revision', icon: RotateCcw },
    };
    return statuses[status] || statuses.not_started;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48 md:col-span-2" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { student, quranic_progress, evaluations, grades, payments, bulletins, stats } = data;
  const typeBadge = getStudentTypeBadge(student.student_type);
  const TypeIcon = typeBadge.icon;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="student-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/students')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {student.first_name[0]}{student.last_name[0]}
              </span>
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">{student.first_name} {student.last_name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">{student.matricule}</Badge>
                <Badge variant="outline" className={cn('text-xs gap-1', typeBadge.class)}>
                  <TypeIcon className="w-3 h-3" />{typeBadge.label}
                </Badge>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Bouton Certificat de Scolarité */}
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={() => handleDownloadCertificate(student.student_type === 'classical' ? 'classical' : 'quranic')}
            disabled={downloadingCert !== null}
            data-testid="cert-btn"
          >
            {downloadingCert ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Certificat de Scolarité
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => navigate(`/students/${id}/edit`)}>
            <Edit className="w-4 h-4 mr-2" />Modifier
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.acquired_surahs}</p>
                <p className="text-xs text-muted-foreground">Sourates acquises</p>
              </div>
            </div>
            <Progress value={stats.quranic_progress_percent} className="mt-3 h-1.5" />
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_evaluations}</p>
                <p className="text-xs text-muted-foreground">Évaluations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-lg font-bold">{formatCurrency(stats.total_paid)}</p>
                <p className="text-xs text-muted-foreground">Payé</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", stats.payment_balance > 0 ? "bg-amber-100" : "bg-green-100")}>
                <TrendingUp className={cn("w-5 h-5", stats.payment_balance > 0 ? "text-amber-600" : "text-green-600")} />
              </div>
              <div>
                <p className="text-lg font-bold">{formatCurrency(stats.payment_balance)}</p>
                <p className="text-xs text-muted-foreground">{stats.payment_balance > 0 ? 'Reste à payer' : 'Soldé'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info">Informations</TabsTrigger>
          <TabsTrigger value="quran">Progression Coranique</TabsTrigger>
          <TabsTrigger value="evaluations">Évaluations</TabsTrigger>
          <TabsTrigger value="payments">Paiements</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">Identité</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Nom complet</span><span className="font-medium">{student.first_name} {student.last_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Date de naissance</span><span className="font-medium">{student.birth_date ? format(new Date(student.birth_date), 'dd MMMM yyyy', { locale: fr }) : '-'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Lieu de naissance</span><span className="font-medium">{student.birth_place || '-'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Genre</span><span className="font-medium">{student.gender === 'male' ? 'Garçon' : 'Fille'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Niveau coranique</span><span className="font-medium">{getQuranicLevelLabel(student.quranic_level)}</span></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">Parents</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {student.parent_info?.father_name && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{student.parent_info.father_name}</p>
                        <p className="text-sm text-muted-foreground">Père {student.parent_info.father_profession && `• ${student.parent_info.father_profession}`}</p>
                        {student.parent_info.father_phone && <p className="text-sm flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{student.parent_info.father_phone}</p>}
                      </div>
                      {student.parent_info.father_phone && (
                        <WhatsAppContact student={student} parentType="father" />
                      )}
                    </div>
                  </div>
                )}
                {student.parent_info?.mother_name && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{student.parent_info.mother_name}</p>
                        <p className="text-sm text-muted-foreground">Mère {student.parent_info.mother_profession && `• ${student.parent_info.mother_profession}`}</p>
                        {student.parent_info.mother_phone && <p className="text-sm flex items-center gap-1 mt-1"><Phone className="w-3 h-3" />{student.parent_info.mother_phone}</p>}
                      </div>
                      {student.parent_info.mother_phone && (
                        <WhatsAppContact student={student} parentType="mother" />
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Quran Tab */}
        <TabsContent value="quran">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Progression des sourates</CardTitle>
              <CardDescription>{stats.acquired_surahs} acquises • {stats.in_progress_surahs} en cours • {stats.quranic_progress_percent}% complété</CardDescription>
            </CardHeader>
            <CardContent>
              {quranic_progress.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune progression enregistrée</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {quranic_progress.map((p) => {
                    const statusInfo = getStatusInfo(p.status);
                    return (
                      <div key={p.id} className={cn("p-3 rounded-lg border text-center", statusInfo.class)}>
                        <p className="font-medium text-sm truncate">{p.surah_id}</p>
                        <p className="text-xs mt-1">{p.verses_memorized} versets</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evaluations Tab */}
        <TabsContent value="evaluations">
          <Card>
            <CardHeader><CardTitle className="text-lg">Historique des évaluations</CardTitle></CardHeader>
            <CardContent>
              {evaluations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune évaluation</p>
              ) : (
                <div className="space-y-3">
                  {evaluations.slice(0, 10).map((e) => (
                    <div key={e.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{e.evaluation_date}</p>
                        <p className="text-sm text-muted-foreground">{e.surah_evaluated || 'Évaluation générale'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">{((e.memorization_score + e.recitation_score + e.discipline_score + e.behavior_score) / 4).toFixed(1)}/20</p>
                        <p className="text-xs text-muted-foreground">Moyenne</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader><CardTitle className="text-lg">Historique des paiements</CardTitle></CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucun paiement</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium capitalize">{p.month || 'Inscription'}</p>
                        <p className="text-sm text-muted-foreground">{p.payment_date || '-'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(p.amount_paid)} / {formatCurrency(p.amount_due)}</p>
                        <Badge variant="outline" className={cn("text-xs", p.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700')}>
                          {p.status === 'paid' ? 'Payé' : 'En attente'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
